<template>
  <div>

    <!-- Income Categories: combines the PDF's "Income Category Summary" (amounts) and
         "Income Categories" (calculation detail) sections into a single table, since on the
         web page there's no pagination constraint requiring them to be split. -->
    <h4 class="mt-0 mb-3 se-bp-message-title">Income Categories</h4>

    <div class="table-responsive table-full-width se-wage-findings-table mt-1 ml-0">
      <table class="se-wage-table">
        <thead>
        <tr>
          <th class="se-wage-col-category">Category</th>
          <th class="se-wage-col-amount">Amount</th>
          <th class="se-wage-col-amount">Opportunity Amount</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in incomeCategoryRows" :key="row.category">
          <td class="se-wage-col-category">{{row.categoryLabel}}</td>
          <td class="se-wage-col-amount">
            {{$filters.toCurrency(row.monthlyAmount, true)}}
            <div class="se-wage-detail-lines" v-if="row.detailLines.length">
              <div v-for="(line, idx) in row.detailLines" :key="idx">{{line}}</div>
            </div>
          </td>
          <td class="se-wage-col-amount">
            <template v-if="row.showOpportunityAmount">
              {{$filters.toCurrency(row.monthlyOpportunityAmount, true)}}
              <div class="se-wage-detail-lines" v-if="row.opportunityDetailLines.length">
                <div v-for="(line, idx) in row.opportunityDetailLines" :key="idx">{{line}}</div>
              </div>
            </template>
          </td>
        </tr>
        <tr class="se-wage-total-row">
          <td class="se-wage-col-category">Total</td>
          <td class="se-wage-col-amount">{{$filters.toCurrency(totalMonthlyAmount, true)}}</td>
          <td class="se-wage-col-amount">{{$filters.toCurrency(totalMonthlyOpportunityAmount, true)}}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Income Details: employer-level YTD and Prior Year pay/income breakdown -->
    <template v-if="ytdEmployerIncomeDetails.length || priorYearEmployerIncomeDetails.length">
      <h4 class="mt-4 mb-3 se-bp-message-title">Income Details</h4>

      <div v-for="(employerDetails, idx) in ytdEmployerIncomeDetails" :key="'ytd-'+idx">
        <se-eval-findings-wage-employer-detail
          section-title="Year-to-Date Income Detail"
          :employer-details="employerDetails"/>
      </div>

      <div v-for="(employerDetails, idx) in priorYearEmployerIncomeDetails" :key="'py-'+idx">
        <se-eval-findings-wage-employer-detail
          section-title="Prior Year Income Detail"
          :employer-details="employerDetails"
          :show-w2="true"/>
      </div>
    </template>

  </div>
</template>
<script>
import { defineComponent } from 'vue';
import {mapGetters, mapState} from "vuex";
import Enums from 'src/model/enums'
import {toCurrency} from 'src/util/filters'
import SeEvalFindingsWageEmployerDetail from './SeEvalFindingsWageEmployerDetail.vue'

export default defineComponent({

  components: {
    SeEvalFindingsWageEmployerDetail
  },

  data() {
    return {
      Enums
    }
  },

  computed: {
    ...mapState({
      seRequest: state => state.se.request,
      seResponse: state => state.se.response
    }),
    ...mapGetters(['isWageEarner']),

    wageData() {
      return this.seResponse.wageData ?? []
    },

    comparativeCategoryItems() {
      return this.seResponse.details?.comparativeWageEarnerIncomeAnalysis?.categoryItems ?? []
    },

    ytdEmployerIncomeDetails() {
      return this.seResponse.details?.wageEarnerDetails?.ytdEmployerIncomeDetails ?? []
    },

    priorYearEmployerIncomeDetails() {
      return this.seResponse.details?.wageEarnerDetails?.priorYearEmployerIncomeDetails ?? []
    },

    incomeCategoryRows() {
      return [...this.wageData]
        .filter(c => c && c.category)
        .sort((a, b) => this.categoryOrderIndex(a.category) - this.categoryOrderIndex(b.category))
        .map(category => {
          let categoryLabel = Enums.SeWageResponseCategoryTypeLabel[category.category] || category.category

          if (category.category === Enums.SeWageIncomeItemCategoryType.base && category.categoryDetails?.basisType) {
            if (category.categoryDetails.basisType === Enums.SeEmployeeBasisType.fixed) {
              categoryLabel += ' (Fixed)'
            } else if (category.categoryDetails.basisType === Enums.SeEmployeeBasisType.variable) {
              categoryLabel += ' (Variable: fluctuating hours or rate)'
            }
          }

          let regularAmount = category.monthlyAmount
          let opportunityAmount = category.monthlyOpportunityAmount
          let showOpportunityAmount = opportunityAmount !== null && opportunityAmount !== undefined
            && (regularAmount === null || regularAmount === undefined || Number(opportunityAmount) !== Number(regularAmount))

          return {
            category: category.category,
            categoryLabel,
            monthlyAmount: category.monthlyAmount,
            monthlyOpportunityAmount: category.monthlyOpportunityAmount,
            showOpportunityAmount,
            detailLines: this.buildDetailLines(category.categoryDetails, category.monthlyAmount, false, category.category),
            opportunityDetailLines: showOpportunityAmount
              ? this.buildDetailLines(category.categoryOpportunityDetails, category.monthlyOpportunityAmount, true, category.category)
              : []
          }
        })
    },

    totalMonthlyAmount() {
      return this.wageData.reduce((sum, c) => sum + (Number(c?.monthlyAmount) || 0), 0)
    },

    totalMonthlyOpportunityAmount() {
      return this.wageData.reduce((sum, c) => sum + (Number(c?.monthlyOpportunityAmount) || 0), 0)
    }
  },

  methods: {
    categoryOrderIndex(category) {
      let idx = Enums.SeWageResponseCategoryOrder.indexOf(category)
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx
    },

    buildCalculationLine(details, monthlyAmount) {
      if (!details || !details.method || details.numberOfMonths === null || details.numberOfMonths === undefined) {
        return ''
      }
      let numberOfMonths = Number(details.numberOfMonths).toFixed(2)
      let cur = v => toCurrency(v, true)

      switch (details.method) {
        case 'fixedPeriodAmount':
          return details.fixedAnnualAmount !== null && details.fixedAnnualAmount !== undefined
            ? `${cur(details.fixedAnnualAmount)} / ${numberOfMonths} = ${cur(monthlyAmount)}` : ''
        case 'hourlyRate':
          return (details.fixedHourlyAmount !== null && details.fixedHourlyAmount !== undefined
            && details.hoursCount !== null && details.hoursCount !== undefined
            && details.frequencyCount !== null && details.frequencyCount !== undefined)
            ? `${cur(details.fixedHourlyAmount)} * ${details.hoursCount} * ${details.frequencyCount} / ${numberOfMonths} = ${cur(monthlyAmount)}` : ''
        case 'averagePriorYear':
          return details.priorYearAmount !== null && details.priorYearAmount !== undefined
            ? `${cur(details.priorYearAmount)} / ${numberOfMonths} = ${cur(monthlyAmount)}` : ''
        case 'averageYearToDate':
          return details.yearToDateAmount !== null && details.yearToDateAmount !== undefined
            ? `${cur(details.yearToDateAmount)} / ${numberOfMonths} = ${cur(monthlyAmount)}` : ''
        case 'averageYearToDatePlusPriorYear':
          return (details.yearToDateAmount !== null && details.yearToDateAmount !== undefined
            && details.priorYearAmount !== null && details.priorYearAmount !== undefined)
            ? `( ${cur(details.yearToDateAmount)} + ${cur(details.priorYearAmount)} ) / ${numberOfMonths} = ${cur(monthlyAmount)}` : ''
        case 'seeMessages':
          return 'See messages'
        default:
          return ''
      }
    },

    buildDetailLines(details, monthlyAmount, isOpportunity, categoryType) {
      if (!details) return []
      let lines = []
      let skipForZeroOpportunity = isOpportunity && (!monthlyAmount || Number(monthlyAmount) === 0)

      if (!skipForZeroOpportunity) {
        let calcLine = this.buildCalculationLine(details, monthlyAmount)
        if (calcLine) lines.push(calcLine)

        if (details.yearToDateAmount !== null && details.yearToDateAmount !== undefined) {
          lines.push(`YTD: ${toCurrency(details.yearToDateAmount, true)}`)
        }
        if (details.priorYearAmount !== null && details.priorYearAmount !== undefined) {
          lines.push(`Prior year: ${toCurrency(details.priorYearAmount, true)}`)
        }
        if (details.method) {
          lines.push(`Method: ${Enums.SeWageCategoryMethodTypeLabel[details.method] || details.method}`)
        }
      }

      if (!isOpportunity && details.trend) {
        let trendLine = `Trend: ${Enums.SeWageCategoryTrendTypeLabel[details.trend] || details.trend}`
        let matchingItem = this.comparativeCategoryItems.find(item => item.category === categoryType)
        if (matchingItem && matchingItem.change !== null && matchingItem.change !== undefined) {
          trendLine += ` ${this.$filters.toPercent(matchingItem.change)}`
        }
        lines.push(trendLine)
      }

      return lines
    }
  }
});
</script>
<style scoped>
.se-wage-findings-table {
  border: 0;
}

.se-wage-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1rem;
}

.se-wage-table th {
  background-color: rgba(229,248,255,1.0);
  color: rgba(0,0,0,0.75);
  font-weight: 700;
  text-align: left;
  padding: 10px;
  border: 1px solid #E4E7ED;
}

.se-wage-table .se-wage-col-amount {
  text-align: right;
}

.se-wage-table td {
  background-color: #F5F7FA;
  padding: 8px 10px;
  border: 1px solid #E4E7ED;
  vertical-align: top;
}

.se-wage-table .se-wage-total-row td {
  background-color: rgba(229,248,255,1.0);
  font-weight: 700;
}

.se-wage-detail-lines {
  margin-top: 4px;
  font-size: 0.90rem;
  font-weight: 400;
  color: rgba(0,0,0,0.65);
  text-align: left;
}
</style>


